import classNames from 'classnames';
import { Key, RefObject } from 'react';
import { FormattedMessage } from 'react-intl';
import Picture from '@alltrails/core/components/Picture';
import Typography from '@alltrails/core/components/Typography';
import useToggle from '@alltrails/core/hooks/useToggle';
import Heading from '@alltrails/denali/components/Heading';
import CheckCircleFilled from '@alltrails/denali/icons/CheckCircleFilled';
import Chevron from '@alltrails/denali/icons/Chevron';
import type { Plan } from '@alltrails/types/Plan';
import GiftPlan from '../GiftPlan';
import styles from './GiftingHeader.module.scss';
import hero768wAvifSrc from './hero-768w.avif';
import heroAvifSrc from './hero.avif';

type GiftingHeaderProps = {
  plan: Plan;
  buyGiftButtonRef: RefObject<HTMLAnchorElement | null>;
};

const features = [
  // eslint-disable-next-line react/jsx-key
  <FormattedMessage defaultMessage="Download maps to stay on track without service" />,
  // eslint-disable-next-line react/jsx-key
  <FormattedMessage defaultMessage="Never miss a turn with wrong-turn alerts" />,
  // eslint-disable-next-line react/jsx-key
  <FormattedMessage defaultMessage="Keep your crew in the loop with Live Share" />,
  // eslint-disable-next-line react/jsx-key
  <FormattedMessage defaultMessage="Know what to expect with real-time map overlays" />
];

// Desktop: https://www.figma.com/file/gZaqAej8HzoFkyrjlQ4cNs/Integrating-Brand-Changes?node-id=586%3A30927
// Mobile: https://www.figma.com/file/gZaqAej8HzoFkyrjlQ4cNs/Integrating-Brand-Changes?node-id=5958%3A97462
const GiftingHeader = ({ plan, buyGiftButtonRef }: GiftingHeaderProps) => {
  const [isOpen, toggle] = useToggle();

  return (
    <section className={styles.section}>
      <div className={styles.content}>
        <div className={styles.featuresAndPlan}>
          <div className={styles.features}>
            <Heading as="h1" className={styles.heading} size="700">
              <FormattedMessage defaultMessage="Give the gift of adventure" />
            </Heading>
            <div>
              <div className={styles.toggle}>
                <button className={styles.button} onClick={toggle} type="button">
                  <Typography color="inverse" variant="heading200">
                    <FormattedMessage defaultMessage="Features designed for all" />
                  </Typography>
                  <Chevron size="sm" orientation={isOpen ? 'up' : 'down'} color="--color-text-primary-inverse" />
                </button>
              </div>
              <ul className={classNames(styles.ul, isOpen && styles.openul)}>
                {features.map((feature, index) => (
                  <li key={index as Key}>
                    <CheckCircleFilled color="--color-text-plus" />
                    <Typography color="inverse" variant="text200">
                      {feature}
                    </Typography>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className={classNames(styles.plan, isOpen && styles.open)}>
            <GiftPlan plan={plan} buyGiftButtonRef={buyGiftButtonRef} />
          </div>
        </div>
        <Picture alt="" className={styles.picture} sources={[{ media: '(max-width: 767.95px)', src: hero768wAvifSrc }, { src: heroAvifSrc }]} />
      </div>
    </section>
  );
};

export default GiftingHeader;
